/**
 * Canonical tile definitions for the .pocketgolf course format.
 *
 * This file is the map editor's self-contained copy of the tile table. It used to be
 * imported from the React Native app (`../src/constants`), which coupled the editor's
 * build to the app and forced a `react-native` module alias just to satisfy an unused
 * `Dimensions` import. The editor now owns its own copy.
 *
 * `mapChar` is the on-disk representation: each grid row in a .pocketgolf file is a
 * string of these characters, as long as the hole is wide (see grid.js). Changing a
 * `mapChar` breaks every existing course file, so don't. The physics fields are not used by the editor itself, but are
 * kept here as documentation of what each tile does in game.
 *
 * The Swift game (PocketGolf2) has its own copy in Sources/PocketGolfCore/TileType.swift.
 * The two must agree on `mapChar`; keep them in sync.
 */ /** Insertion order determines the order of the editor's tile palette. */ const $8d78eed0340873cb$export$34ddeca23ac38a20 = {
    TEE: {
        mapChar: 'X',
        mapTitle: 'TeeBox',
        editorIcon: "\uD83C\uDFCC\uFE0F\u200D\u2642\uFE0F",
        bounce: 1.0,
        friction: 0,
        accuracy: 1,
        color: '#45CD39',
        skillSpeedMultiplier: 1.0
    },
    FAIRWAY: {
        mapChar: 'F',
        mapTitle: 'Fairway',
        editorIcon: null,
        bounce: 1.0,
        friction: 0,
        accuracy: 0.95,
        color: '#45CD39',
        skillSpeedMultiplier: 1.0
    },
    ROUGH: {
        mapChar: 'R',
        mapTitle: 'Rough',
        editorIcon: null,
        bounce: 0.8,
        friction: 0.3,
        accuracy: 0.75,
        color: '#009502',
        skillSpeedMultiplier: 1.15
    },
    WATER: {
        mapChar: 'W',
        mapTitle: 'Water',
        editorIcon: null,
        bounce: 0,
        friction: 0,
        accuracy: 1,
        color: '#1E90FF',
        skillSpeedMultiplier: 1.0
    },
    SAND: {
        mapChar: 'S',
        mapTitle: 'Sand',
        editorIcon: null,
        bounce: 0.5,
        friction: 0.45,
        accuracy: 0.7,
        color: '#F4A460',
        skillSpeedMultiplier: 1.4
    },
    GREEN: {
        mapChar: 'G',
        mapTitle: 'Green',
        editorIcon: null,
        bounce: 1.0,
        friction: -0.1,
        accuracy: 0.95,
        color: '#20C237',
        skillSpeedMultiplier: 0.85
    },
    HOLE: {
        mapChar: '0',
        mapTitle: 'Hole',
        editorIcon: "\u26F3\uFE0F",
        bounce: 0,
        friction: 1,
        accuracy: 1,
        color: '#20C237',
        skillSpeedMultiplier: 0.85
    },
    TREE: {
        mapChar: 'T',
        mapTitle: 'Tree',
        editorIcon: "\uD83C\uDF32",
        bounce: 0.7,
        friction: 0.1,
        accuracy: 0.75,
        color: '#4ba14b',
        hitChance: 0.07,
        skillSpeedMultiplier: 1.1
    },
    DENSE_TREE: {
        mapChar: 'Y',
        mapTitle: 'DenseTree',
        editorIcon: "\uD83C\uDF33",
        bounce: 0.7,
        friction: 0.3,
        accuracy: 0.5,
        color: '#009502',
        hitChance: 0.5,
        skillSpeedMultiplier: 1.2
    },
    PATHWAY: {
        mapChar: 'P',
        mapTitle: 'Pathway',
        editorIcon: "\uD83D\uDEE4\uFE0F",
        bounce: 1.8,
        friction: 0.4,
        accuracy: 1,
        color: '#2f2f2f'
    },
    ROCK: {
        mapChar: 'K',
        mapTitle: 'Rock',
        editorIcon: "\uD83E\uDEA8",
        bounce: 4,
        friction: 0.8,
        accuracy: 0.6,
        color: '#7C7C7C',
        skillSpeedMultiplier: 1.0
    },
    BUSH: {
        mapChar: 'B',
        mapTitle: 'Bush',
        editorIcon: "\uD83C\uDF3F",
        bounce: 0.5,
        friction: 0.8,
        accuracy: 0.6,
        lostChance: 0.3,
        color: '#0f5e17',
        skillSpeedMultiplier: 1.0
    },
    OUT_OF_BOUNDS: {
        mapChar: 'O',
        mapTitle: 'OutOfBounds',
        editorIcon: null,
        bounce: 0,
        friction: 1,
        accuracy: 0.6,
        color: '#555555',
        skillSpeedMultiplier: 1.0
    }
};
var $8d78eed0340873cb$export$2e2bcd8739ae039 = $8d78eed0340873cb$export$34ddeca23ac38a20;


/**
 * How big a hole is.
 *
 * Width used to be a constant. Every .pocketgolf hole was 20 tiles across, so the editor, the format
 * and the game all agreed on the number without any of them having to say it, and `GRID_SIZE_X` could
 * stand in for "the width of this hole" anywhere it was needed. A v2 hole now declares its own width by
 * how long its rows are — the same way it has always declared its height by how many rows it has — so
 * the number is read off the grid instead of assumed.
 *
 * That is why this module has no `width` field to set: the rows *are* the width. Resizing rewrites
 * them, and everything downstream measures what it finds. A stored width could disagree with the data,
 * and a hole whose declared width disagrees with its rows is the bug where the editor paints a column
 * the game will never read.
 *
 * ## The bounds
 *
 * `WIDTH_RANGE` and `HEIGHT_RANGE` are what the editor holds an author to. The game is more forgiving
 * than this — it crops a hole wider than the maximum rather than refusing the file, and plays a hole
 * narrower than the minimum as it finds it — because a parser's job is to make a file playable and an
 * editor's job is to not write one that needs rescuing.
 *
 * `DEFAULT_WIDTH` is different in kind from the other three: it is the width v1 froze, so it is both
 * the width a new hole starts at and the width the game plays *every* v1 hole at, whatever its rows
 * say. A v1 course must stay at it.
 *
 * The game's copy of these numbers is `Physics.gridWidth`, `Physics.gridWidthRange`,
 * `Physics.maxGridHeight` and `Physics.minGridHeight` in Sources/PocketGolfCore/Club.swift. The two
 * must agree; keep them in sync.
 */ /** The width every v1 hole is played at, and the width a new hole starts at. */ const $628bcc8876065384$export$aa4eace044cdfdbf = 20;
const $628bcc8876065384$export$611c894df53833b0 = 16;
const $628bcc8876065384$export$fcb4e206d7a8f717 = {
    min: 15,
    max: 50
};
const $628bcc8876065384$export$dcbc037c875a37c7 = {
    min: 15,
    max: 150
};
const $628bcc8876065384$export$d9ad6648a5e4039e = 'R';
function $628bcc8876065384$export$2f192c6eccb63fc0(grid) {
    if (!Array.isArray(grid) || grid.length === 0) return 0;
    return typeof grid[0] === 'string' ? grid[0].length : 0;
}
function $628bcc8876065384$export$e9f3dfb19daa974(value, range) {
    if (!Number.isFinite(value)) return range.min;
    return Math.max(range.min, Math.min(range.max, Math.trunc(value)));
}
function $628bcc8876065384$export$5944653400dd6092(width = $628bcc8876065384$export$aa4eace044cdfdbf, height = $628bcc8876065384$export$611c894df53833b0) {
    return Array.from({
        length: height
    }, ()=>$628bcc8876065384$export$d9ad6648a5e4039e.repeat(width));
}
function $628bcc8876065384$export$67f805715e25b698(grid, width, height, fill = $628bcc8876065384$export$d9ad6648a5e4039e) {
    const source = Array.isArray(grid) ? grid : [];
    return Array.from({
        length: height
    }, (_, y)=>{
        const row = typeof source[y] === 'string' ? source[y] : '';
        return row.length >= width ? row.slice(0, width) : row + fill.repeat(width - row.length);
    });
}


/**
 * Green contours for the .pocketgolf v2 format.
 *
 * A v2 hole may carry a `heights` array alongside its `grid`: one string per row, one character per
 * cell, `0`–`9`. Only cells that are green or the cup are read; everything else in the matrix is
 * ignored, which is why the rest of a row can be zeros.
 *
 * Heights rather than slope vectors, because a height field cannot describe an impossible green. A
 * slope field can be authored so every cell drains into its neighbour in a loop, and a ball on it
 * would roll forever without ever losing elevation. Only *differences* matter, so a green written
 * `3 4 5 5` plays exactly like one written `0 1 2 2`.
 *
 * The game's copy of this arithmetic is Sources/PocketGolfCore/GreenContour.swift. The two must
 * agree, or the arrows drawn here point somewhere the ball does not go — which is worse than drawing
 * no arrows at all. Keep them in sync.
 */ 
const $490d5ef8e3640c94$export$70d33a2740b029ae = 0;
const $490d5ef8e3640c94$export$daa2496be4772440 = 9;
/** Tiles a putt rolls across. The cup is included so a green has no hole in its contour. */ const $490d5ef8e3640c94$var$PUTTING_SURFACE_CHARS = [
    'G',
    '0'
];
function $490d5ef8e3640c94$export$aa504d226fe5898d(char) {
    return $490d5ef8e3640c94$var$PUTTING_SURFACE_CHARS.includes(char);
}
function $490d5ef8e3640c94$export$35ad13e61d9747bd(rows, width = (0, $628bcc8876065384$export$aa4eace044cdfdbf)) {
    return Array.from({
        length: rows
    }, ()=>'0'.repeat(width));
}
function $490d5ef8e3640c94$export$cc650f51529c4068(heights, grid) {
    const source = Array.isArray(heights) ? heights : [];
    const width = (0, $628bcc8876065384$export$2f192c6eccb63fc0)(grid);
    return grid.map((_, y)=>{
        const row = typeof source[y] === 'string' ? source[y] : '';
        let out = '';
        for(let x = 0; x < width; x++)out += String($490d5ef8e3640c94$export$4465cd453867bc23($490d5ef8e3640c94$var$parseHeight(row.charAt(x))));
        return out;
    });
}
function $490d5ef8e3640c94$var$parseHeight(char) {
    const value = parseInt(char, 10);
    return Number.isFinite(value) ? value : 0;
}
function $490d5ef8e3640c94$export$4465cd453867bc23(value) {
    return Math.max($490d5ef8e3640c94$export$70d33a2740b029ae, Math.min($490d5ef8e3640c94$export$daa2496be4772440, value));
}
function $490d5ef8e3640c94$export$4441a3ad4191b9e1(grid, heights, x, y) {
    if (y < 0 || y >= grid.length || x < 0 || x >= (0, $628bcc8876065384$export$2f192c6eccb63fc0)(grid)) return null;
    if (!$490d5ef8e3640c94$export$aa504d226fe5898d(grid[y].charAt(x))) return null;
    const row = heights[y] || '';
    return $490d5ef8e3640c94$export$4465cd453867bc23($490d5ef8e3640c94$var$parseHeight(row.charAt(x)));
}
function $490d5ef8e3640c94$export$69e89bcf967dbd74(grid, heights, x, y) {
    const centre = $490d5ef8e3640c94$export$4441a3ad4191b9e1(grid, heights, x, y);
    if (centre === null) return null;
    const sample = (dx, dy)=>{
        const value = $490d5ef8e3640c94$export$4441a3ad4191b9e1(grid, heights, x + dx, y + dy);
        return value === null ? centre : value;
    };
    // Uphill gradient, then negated: the slope points the way the ball is pulled.
    const dx = -(sample(1, 0) - sample(-1, 0)) / 2;
    const dy = -(sample(0, 1) - sample(0, -1)) / 2;
    const steepness = Math.sqrt(dx * dx + dy * dy);
    let angle = Math.atan2(dx, -dy) * 180 / Math.PI;
    if (angle < 0) angle += 360;
    return {
        dx: dx,
        dy: dy,
        steepness: steepness,
        angle: angle
    };
}
function $490d5ef8e3640c94$export$18b7c2a79fb7b9ec(grid, heights) {
    let low = Infinity;
    let high = -Infinity;
    const width = (0, $628bcc8876065384$export$2f192c6eccb63fc0)(grid);
    for(let y = 0; y < grid.length; y++)for(let x = 0; x < width; x++){
        const value = $490d5ef8e3640c94$export$4441a3ad4191b9e1(grid, heights, x, y);
        if (value === null) continue;
        low = Math.min(low, value);
        high = Math.max(high, value);
    }
    return low <= high ? {
        low: low,
        high: high
    } : null;
}
function $490d5ef8e3640c94$export$bd477a8154a23014(grid, heights) {
    const range = $490d5ef8e3640c94$export$18b7c2a79fb7b9ec(grid, heights);
    return range !== null && range.low < range.high;
}
function $490d5ef8e3640c94$export$7919ba25f6145df6(steepness) {
    if (steepness < 0.3) return 'Flat';
    if (steepness < 0.75) return 'Gentle';
    if (steepness < 1.25) return 'Moderate';
    return 'Severe';
}


/**
 * Whole-hole terrain elevation for the .pocketgolf v2 format.
 *
 * A v2 hole may carry an `elevation` array alongside its `grid`: one string per row, one character per
 * cell, `0`–`9`. Unlike `heights`, which is read only on the putting surface, **every** cell is read —
 * this is the ground the whole hole stands on.
 *
 * ## Why this is not just `heights` with a wider reach
 *
 * The two fields mean different things at the same coordinates, and conflating them would silently
 * re-cut every course already authored. `heights` deliberately has no value off the green, so the drop
 * from a green down to the ground around it is not a slope a putt can feel — a green sitting at 3 in a
 * matrix padded with zeros is *correct* under `heights` and a three-step cliff under `elevation`.
 *
 * So they are separate fields. A hole may declare either; where it declares both, the game reads
 * `elevation` and derives the green's contour from it, which is what keeps the approach and the putt
 * agreeing about how high the green stands.
 *
 * ## What a step is worth
 *
 * Five metres, at the game's ten-metres-a-tile scale. A shot loses about 0.3 tiles of carry per step it
 * climbs, a ball runs further downhill than up, and a tree's canopy is measured from the ground it
 * stands on — so a treeline on a rise really is taller: dense trees eight steps up are too tall for any
 * club in the bag to clear.
 *
 * The game's copy of this arithmetic is Sources/PocketGolfCore/TerrainElevation.swift. The two must
 * agree, or the shading and arrows drawn here describe a hole the ball does not play. Keep them in sync.
 */ 

const $f135f857aa650103$export$84cd9b1ff2925cc4 = 4;
const $f135f857aa650103$export$ed0b1b5a2fd57fa7 = 5;
function $f135f857aa650103$var$parseStep(char) {
    const value = parseInt(char, 10);
    return Number.isFinite(value) ? value : null;
}
function $f135f857aa650103$export$3fbf83d1255045e9(elevation, grid, fill = $f135f857aa650103$export$84cd9b1ff2925cc4) {
    const source = Array.isArray(elevation) ? elevation : [];
    const padding = (0, $490d5ef8e3640c94$export$4465cd453867bc23)(fill);
    const width = (0, $628bcc8876065384$export$2f192c6eccb63fc0)(grid);
    return grid.map((_, y)=>{
        const row = typeof source[y] === 'string' ? source[y] : '';
        let out = '';
        for(let x = 0; x < width; x++){
            const parsed = $f135f857aa650103$var$parseStep(row.charAt(x));
            out += String(parsed === null ? padding : (0, $490d5ef8e3640c94$export$4465cd453867bc23)(parsed));
        }
        return out;
    });
}
function $f135f857aa650103$export$e9062493a4c122a9(rows, step = $f135f857aa650103$export$84cd9b1ff2925cc4, width = (0, $628bcc8876065384$export$aa4eace044cdfdbf)) {
    return Array.from({
        length: rows
    }, ()=>String((0, $490d5ef8e3640c94$export$4465cd453867bc23)(step)).repeat(width));
}
function $f135f857aa650103$export$c3084f1cf8af9a05(elevation, x, y) {
    if (!Array.isArray(elevation) || elevation.length === 0) return 0;
    const row = elevation[Math.max(0, Math.min(y, elevation.length - 1))] || '';
    const parsed = $f135f857aa650103$var$parseStep(row.charAt(Math.max(0, Math.min(x, row.length - 1))));
    return parsed === null ? 0 : (0, $490d5ef8e3640c94$export$4465cd453867bc23)(parsed);
}
function $f135f857aa650103$export$21a9d435687815aa(elevation, x, y) {
    // The `+ 0` turns negative zero back into zero. Negating a difference of nothing gives `-0` in
    // JavaScript, which compares equal to 0 but prints as "-0" — so a perfectly level hole would show a
    // gradient of "-0.00" in a tooltip and read as a rounding bug.
    const dx = -($f135f857aa650103$export$c3084f1cf8af9a05(elevation, x + 1, y) - $f135f857aa650103$export$c3084f1cf8af9a05(elevation, x - 1, y)) / 2 + 0;
    const dy = -($f135f857aa650103$export$c3084f1cf8af9a05(elevation, x, y + 1) - $f135f857aa650103$export$c3084f1cf8af9a05(elevation, x, y - 1)) / 2 + 0;
    const steepness = Math.sqrt(dx * dx + dy * dy);
    let angle = Math.atan2(dx, -dy) * 180 / Math.PI;
    if (angle < 0) angle += 360;
    return {
        dx: dx,
        dy: dy,
        steepness: steepness,
        angle: angle
    };
}
function $f135f857aa650103$export$1c941da6afed58dd(elevation) {
    if (!Array.isArray(elevation) || elevation.length === 0) return null;
    let low = Infinity;
    let high = -Infinity;
    const width = (0, $628bcc8876065384$export$2f192c6eccb63fc0)(elevation);
    for(let y = 0; y < elevation.length; y++)for(let x = 0; x < width; x++){
        const value = $f135f857aa650103$export$c3084f1cf8af9a05(elevation, x, y);
        low = Math.min(low, value);
        high = Math.max(high, value);
    }
    return low <= high ? {
        low: low,
        high: high
    } : null;
}
function $f135f857aa650103$export$afd9c060cc316883(elevation) {
    const range = $f135f857aa650103$export$1c941da6afed58dd(elevation);
    return range !== null && range.low < range.high;
}
function $f135f857aa650103$export$d531e3ecc3c8ca83(elevation, from, to) {
    const steps = $f135f857aa650103$export$c3084f1cf8af9a05(elevation, to.x, to.y) - $f135f857aa650103$export$c3084f1cf8af9a05(elevation, from.x, from.y);
    // One step is 0.5 tiles and a tile is 10 m, so a step is 5 m. Both constants live in Physics:
    // elevationStepTiles and metresPerTile.
    return steps * $f135f857aa650103$export$ed0b1b5a2fd57fa7;
}


/**
 * Alternate pin positions for the .pocketgolf v2 format.
 *
 * A v2 hole may carry a `pinPositions` array alongside its `grid`: `{ x, y }` cells the cup may be cut
 * to instead of the one the grid draws. The game picks one at random when a round starts, moves the cup
 * there — grid marker and all — and plays the hole exactly as it would any other. So one green can be
 * three or four different approach shots depending on the day, without three or four copies of the hole.
 *
 * ## Why the grid still has exactly one cup
 *
 * The `0` in the grid is what every reader means by "the hole": the flag, the camera, the TO HOLE
 * readout, the cup-capture radius and the import validation all follow it. Painting three of them would
 * give a hole three flags a ball could drop into, which is a different game — and the editor already
 * warns about a second cup for that reason. The alternates are declared beside the grid instead, and the
 * one cup *moves* to whichever is drawn.
 *
 * That is also why the cup itself is never stored here: it is already a candidate by virtue of being
 * painted, and storing it twice would make that flag likelier than the others for no visible reason.
 *
 * The game's copy of these rules is `Level.pinPositions` and `Level.cut(to:)` in
 * Sources/PocketGolfCore/Course.swift. The two must agree.
 */ 
function $45c5256a35480592$export$7ae14d40dd07007c(grid) {
    for(let y = 0; y < grid.length; y++){
        const x = (grid[y] || '').indexOf('0');
        if (x >= 0) return {
            x: x,
            y: y
        };
    }
    return null;
}
function $45c5256a35480592$export$7904b8e4f0b7816f(pins, grid) {
    if (!Array.isArray(pins)) return [];
    const cup = $45c5256a35480592$export$7ae14d40dd07007c(grid || []);
    const rows = (grid || []).length;
    const columns = (0, $628bcc8876065384$export$2f192c6eccb63fc0)(grid || []);
    const seen = new Set();
    const kept = [];
    pins.forEach((pin)=>{
        if (!pin || typeof pin !== 'object') return;
        const x = Math.trunc(Number(pin.x));
        const y = Math.trunc(Number(pin.y));
        if (!Number.isFinite(x) || !Number.isFinite(y)) return;
        if (x < 0 || x >= columns || y < 0 || y >= rows) return;
        if (cup && cup.x === x && cup.y === y) return;
        const key = `${x},${y}`;
        if (seen.has(key)) return;
        seen.add(key);
        kept.push({
            x: x,
            y: y
        });
    });
    return kept;
}
function $45c5256a35480592$export$dc0d615537fa77c7(pins, x, y) {
    return $45c5256a35480592$var$normalisePinList(pins).some((pin)=>pin.x === x && pin.y === y);
}
function $45c5256a35480592$export$14f2f3b8de00530c(pins, x, y) {
    const list = $45c5256a35480592$var$normalisePinList(pins);
    const existing = list.findIndex((pin)=>pin.x === x && pin.y === y);
    if (existing >= 0) return list.filter((_, index)=>index !== existing);
    return [
        ...list,
        {
            x: x,
            y: y
        }
    ];
}
/** Whatever was stored, as a list of integer cells. Defensive: the field is hand-editable. */ function $45c5256a35480592$var$normalisePinList(pins) {
    if (!Array.isArray(pins)) return [];
    return pins.filter((pin)=>pin && typeof pin === 'object').map((pin)=>({
            x: Math.trunc(Number(pin.x)),
            y: Math.trunc(Number(pin.y))
        })).filter((pin)=>Number.isFinite(pin.x) && Number.isFinite(pin.y));
}


/**
 * Reading and writing the .pocketgolf course format.
 *
 * Pure functions over a plain course object, deliberately free of the DOM: the shape of the file is the
 * contract with the game, and a contract worth having is one a test can hold you to. `editor.js` keeps
 * the clicking and the rendering.
 *
 * The format has two generations, and which one a file is written in decides what it may carry:
 *
 * | | v1 | v2 |
 * |---|---|---|
 * | `grid` | yes | yes |
 * | `wind` | ignored | yes |
 * | `heights` (green contour) | ignored | yes |
 * | `elevation` (whole-hole terrain) | ignored | yes |
 * | `pinPositions` (alternate cups) | ignored | yes |
 *
 * v1 is what every course already in the world is. The game reads a missing `formatVersion` as v1 and
 * ignores v2-only fields on a v1 course, so writing them there would put data in a file that nothing will
 * ever read — which is why they are dropped rather than left in.
 */ 



const $54c87622d515a545$export$a2eb092aef0771b1 = 'pocketgolf_course';
const $54c87622d515a545$export$59fb14b1771cb35f = 2;
function $54c87622d515a545$export$a3d71d718969fe95(courseData) {
    return courseData && courseData.formatVersion === 2 ? 2 : 1;
}
function $54c87622d515a545$export$94dd0941061f868(courseData) {
    return $54c87622d515a545$export$a3d71d718969fe95(courseData) >= 2;
}
function $54c87622d515a545$export$6c4275ae8c690ec1(courseData) {
    const version = $54c87622d515a545$export$a3d71d718969fe95(courseData);
    const exported = {
        ...courseData,
        type: $54c87622d515a545$export$a2eb092aef0771b1,
        formatVersion: version
    };
    if (version >= 2 && courseData.wind) exported.wind = courseData.wind;
    else delete exported.wind;
    exported.levels = (courseData.levels || []).map((level)=>$54c87622d515a545$var$exportLevel(level, version));
    return exported;
}
/**
 * One hole, with its contour and its terrain written only when there is something to write.
 *
 * A dead-flat green is indistinguishable from no contour and the game treats it as none, so a matrix of
 * identical numbers is dropped rather than exported. The same goes for a hole at one uniform step. That
 * keeps a file from claiming a feature it does not use, and keeps the diff of a course nobody sculpted
 * completely empty.
 *
 * Both may be written on the same hole. The game reads `elevation` in preference and derives the green's
 * shape from it, so a hole with terrain does not need `heights` — but a hole may still carry one from
 * before it was sculpted, and dropping somebody's green contour because they raised a hillside would be
 * destroying work they never asked us to touch.
 */ function $54c87622d515a545$var$exportLevel(level, version) {
    const exported = {
        ...level
    };
    delete exported.heights;
    delete exported.elevation;
    delete exported.pinPositions;
    if (version >= 2 && (0, $490d5ef8e3640c94$export$bd477a8154a23014)(level.grid || [], level.heights || [])) exported.heights = (0, $490d5ef8e3640c94$export$cc650f51529c4068)(level.heights, level.grid);
    if (version >= 2 && (0, $f135f857aa650103$export$afd9c060cc316883)(level.elevation || [])) exported.elevation = (0, $f135f857aa650103$export$3fbf83d1255045e9)(level.elevation, level.grid);
    // The extras only. The cup itself is the `0` already in the grid, and a hole whose every alternate has
    // been taken away is a hole with one cup again — so the field goes rather than being written empty.
    const pins = (0, $45c5256a35480592$export$7904b8e4f0b7816f)(level.pinPositions, level.grid || []);
    if (version >= 2 && pins.length > 0) exported.pinPositions = pins;
    return exported;
}
function $54c87622d515a545$export$cc692388a4445984(parsed) {
    if (!parsed || typeof parsed !== 'object') throw new Error('Invalid JSON object.');
    if (parsed.type !== $54c87622d515a545$export$a2eb092aef0771b1) throw new Error(`Invalid file type. Expected type "${$54c87622d515a545$export$a2eb092aef0771b1}".`);
    const version = $54c87622d515a545$export$a3d71d718969fe95(parsed);
    const course = {
        ...parsed,
        formatVersion: version
    };
    if (!Array.isArray(course.levels)) course.levels = [];
    if (version < 2) delete course.wind;
    course.levels = course.levels.map((level)=>{
        const imported = {
            ...level
        };
        if (!Array.isArray(imported.grid) || typeof imported.grid[0] !== 'string') imported.grid = [];
        // Shaped to the grid on the way in, so a hand-edited matrix that is a few characters short loads
        // rather than painting a contour full of holes. Dropped outright on a v1 course, which cannot
        // carry one.
        if (version >= 2 && Array.isArray(imported.heights) && imported.grid.length > 0) imported.heights = (0, $490d5ef8e3640c94$export$cc650f51529c4068)(imported.heights, imported.grid);
        else delete imported.heights;
        if (version >= 2 && Array.isArray(imported.elevation) && imported.grid.length > 0) imported.elevation = (0, $f135f857aa650103$export$3fbf83d1255045e9)(imported.elevation, imported.grid);
        else delete imported.elevation;
        // Same shaping on the way in as on the way out: coordinates off the grid, repeats and the cup
        // itself are dropped rather than carried, so what the editor draws is exactly what the game will
        // draw from. A v1 course cannot carry them at all.
        if (version >= 2) {
            const pins = (0, $45c5256a35480592$export$7904b8e4f0b7816f)(imported.pinPositions, imported.grid);
            if (pins.length > 0) imported.pinPositions = pins;
            else delete imported.pinPositions;
        } else delete imported.pinPositions;
        return imported;
    });
    return course;
}
function $54c87622d515a545$export$45b76f4d03ea4105(courseData) {
    const warnings = [];
    const levels = courseData.levels || [];
    levels.forEach((level, index)=>{
        const label = `Hole ${index + 1}`;
        const grid = level.grid || [];
        const flat = grid.join('');
        const tees = (flat.match(/X/g) || []).length;
        const cups = (flat.match(/0/g) || []).length;
        if (tees === 0) warnings.push(`${label}: no tee (paint one X).`);
        if (tees > 1) warnings.push(`${label}: ${tees} tees \u{2014} the game takes the first.`);
        if (cups === 0) warnings.push(`${label}: no cup (paint one \u{26F3}).`);
        if (cups > 1) warnings.push(`${label}: ${cups} cups \u{2014} the game takes the first.`);
        if (grid.length === 0) warnings.push(`${label}: empty grid.`);
        // Ragged rows first: every width check below reads the first row, so a hole whose rows disagree
        // has to be reported as that rather than as whatever the first one happens to say.
        const width = (0, $628bcc8876065384$export$2f192c6eccb63fc0)(grid);
        if (grid.some((row)=>row.length !== width)) warnings.push(`${label}: its rows are not all the same width (the first is ${width}).`);
        // The game reads a v1 hole at exactly 20 columns whatever its rows say — padding what is short
        // and discarding what is long. A wide v1 hole is therefore not a wide hole, it is a hole the
        // editor draws one way and the game plays another.
        if (width > 0 && width !== (0, $628bcc8876065384$export$aa4eace044cdfdbf) && !$54c87622d515a545$export$94dd0941061f868(courseData)) warnings.push(`${label}: is ${width} wide, but a v1 course plays every hole at ${(0, $628bcc8876065384$export$aa4eace044cdfdbf)} \u{2014} switch the course to v2.`);
        if (width > (0, $628bcc8876065384$export$fcb4e206d7a8f717).max) warnings.push(`${label}: is ${width} wide; the game crops a hole to ${(0, $628bcc8876065384$export$fcb4e206d7a8f717).max}.`);
        if (grid.length > (0, $628bcc8876065384$export$dcbc037c875a37c7).max) warnings.push(`${label}: is ${grid.length} rows tall; the game drops everything past ${(0, $628bcc8876065384$export$dcbc037c875a37c7).max}.`);
        if (!(level.par >= 1)) warnings.push(`${label}: par must be at least 1.`);
        // Every pin is a place the flag may actually stand, so the same two things have to be true of it
        // that are true of the cup: it is on the putting surface, and it is somewhere the author can see.
        // A pin in the rough is not a hard hole, it is an unreachable one.
        const pins = (0, $45c5256a35480592$export$7904b8e4f0b7816f)(level.pinPositions, grid);
        pins.forEach((pin)=>{
            const char = (grid[pin.y] || '').charAt(pin.x);
            if (char !== 'G' && char !== '0') warnings.push(`${label}: alternate pin at ${pin.x}, ${pin.y} is not on the green.`);
        });
        if (pins.length > 0 && !$54c87622d515a545$export$94dd0941061f868(courseData)) warnings.push(`${label}: has alternate pins but the course is v1, so they are ignored.`);
        if (pins.length > 0 && !(0, $45c5256a35480592$export$7ae14d40dd07007c)(grid)) warnings.push(`${label}: has alternate pins but no cup to alternate with.`);
        // A contour on a hole with no green is data the game will never read.
        if (level.heights && !flat.includes('G') && !flat.includes('0')) warnings.push(`${label}: has a slope but no green to apply it to.`);
        // Both matrices on one hole: playable, but the green contour is the one that gets ignored, and an
        // author who painted it and cannot see it in the game deserves to be told why.
        if (level.heights && (0, $f135f857aa650103$export$afd9c060cc316883)(level.elevation || [])) warnings.push(`${label}: has terrain, so its separate green slope is ignored \u{2014} the green takes its shape from the terrain.`);
    });
    if (levels.length === 0) warnings.push('The course has no holes.');
    if (!$54c87622d515a545$export$94dd0941061f868(courseData) && courseData.wind) warnings.push('Wind is set but the course is v1, so it is ignored.');
    return warnings;
}


let $d18200295c6c5d42$var$courseData = {
    id: 'new_course',
    name: 'New Course',
    description: '',
    formatVersion: 2,
    levels: []
};
let $d18200295c6c5d42$var$currentLevelIndex = -1;
let $d18200295c6c5d42$var$selectedTileType = 'ROUGH';
let $d18200295c6c5d42$var$isPainting = false;
/**
 * What a click paints: `tiles` the terrain type, `terrain` the whole-hole ground elevation (the green
 * included — there is no separate contour for it any more, see elevation.js). Only `terrain` is
 * meaningful on a v2 course; `tiles` always is.
 */ let $d18200295c6c5d42$var$editMode = 'tiles';
let $d18200295c6c5d42$var$selectedHeight = 5;
/**
 * Which gesture painting performs: `brush` paints the cell under the pointer, `select` drags out a
 * rectangle for `fillGrid` to act on, `bucket` floods every connected matching cell, `hill` (terrain
 * mode only) sculpts a smooth rise or dip around a dragged-out radius, and `pin` (v2 only) toggles a cell
 * as one of the cups this hole may be cut to.
 *
 * `pin` is the one tool that writes neither matrix — a pin is a coordinate beside the grid, not a tile in
 * it, for the reason spelled out in pins.js: the hole keeps exactly one `0`, and it moves.
 */ let $d18200295c6c5d42$var$currentTool = 'brush';
/** The rectangle `select` has committed, in grid coordinates inclusive on both ends. `null` when none. */ let $d18200295c6c5d42$var$selection = null;
/** Where a `select`/`hill` drag started and where the pointer currently is, while the button is still down. */ let $d18200295c6c5d42$var$dragAnchor = null;
let $d18200295c6c5d42$var$dragCurrent = null;
/** Whether the current mode paints the terrain height matrix. */ function $d18200295c6c5d42$var$isHeightMode() {
    return $d18200295c6c5d42$var$editMode === 'terrain' && $d18200295c6c5d42$var$isV2();
}
/** The format the file will be written in. v1 carries neither wind nor green contours. */ function $d18200295c6c5d42$var$courseFormat() {
    return (0, $54c87622d515a545$export$a3d71d718969fe95)($d18200295c6c5d42$var$courseData);
}
function $d18200295c6c5d42$var$isV2() {
    return (0, $54c87622d515a545$export$94dd0941061f868)($d18200295c6c5d42$var$courseData);
}
// --- Initialization ---
/** Class on `.editor-layout`, and the key its state is remembered under. */ const $d18200295c6c5d42$var$PROPERTIES_COLLAPSED = 'properties-collapsed';
const $d18200295c6c5d42$var$PROPERTIES_ANIMATED = 'properties-animated';
const $d18200295c6c5d42$var$PROPERTIES_STORAGE_KEY = 'pocketGolfEditorPropertiesCollapsed';
function $d18200295c6c5d42$var$init() {
    $d18200295c6c5d42$var$initTileSelector();
    $d18200295c6c5d42$var$initHeightSelector();
    $d18200295c6c5d42$var$setupDragAndDrop();
    // Attempt to load from localStorage
    const saved = localStorage.getItem('pocketGolfCourse');
    if (saved) try {
        $d18200295c6c5d42$var$courseData = JSON.parse(saved);
        $d18200295c6c5d42$var$currentLevelIndex = $d18200295c6c5d42$var$courseData.levels.length > 0 ? 0 : -1;
        $d18200295c6c5d42$var$showSaveStatus('Loaded from LocalStorage');
    } catch (e) {
        console.error('Local storage load failed', e);
        $d18200295c6c5d42$var$createDefaultCourse();
    }
    else $d18200295c6c5d42$var$createDefaultCourse();
    $d18200295c6c5d42$var$renderAll();
    document.addEventListener('mouseup', ()=>{
        if (!$d18200295c6c5d42$var$isPainting) return;
        $d18200295c6c5d42$var$isPainting = false;
        // Brush painting already saved on every cell (see paintCell); select and hill only act now, once
        // the gesture is finished, so the rectangle or the radius is the one the author actually dragged.
        if ($d18200295c6c5d42$var$currentTool === 'select' && $d18200295c6c5d42$var$dragAnchor && $d18200295c6c5d42$var$dragCurrent) {
            $d18200295c6c5d42$var$selection = $d18200295c6c5d42$var$normaliseRect($d18200295c6c5d42$var$dragAnchor, $d18200295c6c5d42$var$dragCurrent);
            $d18200295c6c5d42$var$dragAnchor = null;
            $d18200295c6c5d42$var$dragCurrent = null;
            $d18200295c6c5d42$var$renderGrid();
        } else if ($d18200295c6c5d42$var$currentTool === 'hill' && $d18200295c6c5d42$var$dragAnchor && $d18200295c6c5d42$var$dragCurrent) {
            $d18200295c6c5d42$var$commitHillDig($d18200295c6c5d42$var$dragAnchor, $d18200295c6c5d42$var$dragCurrent);
            $d18200295c6c5d42$var$dragAnchor = null;
            $d18200295c6c5d42$var$dragCurrent = null;
        }
        $d18200295c6c5d42$var$saveState();
    });
    document.addEventListener('keydown', (e)=>{
        if (e.key === 'Escape' && $d18200295c6c5d42$var$selection) $d18200295c6c5d42$var$clearSelection();
    });
}
function $d18200295c6c5d42$var$createDefaultCourse() {
    $d18200295c6c5d42$var$courseData = {
        id: $d18200295c6c5d42$var$generateUniqueId(),
        name: 'New Course',
        description: '',
        // A new course is authored in the current format. v1 exists so the courses already in the world
        // keep playing as they always have — it is not a thing anyone should have to opt *out* of when
        // starting from scratch, and leaving this unset made every new course silently v1.
        formatVersion: (0, $54c87622d515a545$export$59fb14b1771cb35f),
        levels: []
    };
    // Create 9 holes by default
    for(let i = 1; i <= 9; i++)$d18200295c6c5d42$var$courseData.levels.push({
        name: `Hole ${i}`,
        par: 3,
        id: $d18200295c6c5d42$var$computeLevelId($d18200295c6c5d42$var$courseData.id, i),
        grid: (0, $628bcc8876065384$export$5944653400dd6092)()
    });
    $d18200295c6c5d42$var$currentLevelIndex = 0;
}
function $d18200295c6c5d42$var$computeLevelId(courseId, indexStr) {
    let base = courseId || 'course';
    let num = String(indexStr).padStart(2, '0');
    return `${base}-${num}`;
}
function $d18200295c6c5d42$var$generateUniqueId() {
    const timestamp = Date.now().toString(36); // ~8 chars
    const randomPart = Math.random().toString(36).substring(2, 10); // 8 chars
    return (timestamp + randomPart).substring(0, 16);
}
// --- State Management & Saving ---
function $d18200295c6c5d42$var$saveState() {
    $d18200295c6c5d42$var$updateCourseDataOutput();
    localStorage.setItem('pocketGolfCourse', JSON.stringify($d18200295c6c5d42$var$courseData));
    $d18200295c6c5d42$var$showSaveStatus('Saved automatically');
}
function $d18200295c6c5d42$var$showSaveStatus(msg) {
    const status = document.getElementById('saveStatus');
    status.textContent = msg;
    status.style.color = '#238b21';
    setTimeout(()=>{
        status.textContent = '';
    }, 3000);
}
// --- Rendering ---
function $d18200295c6c5d42$var$renderAll() {
    $d18200295c6c5d42$var$updateAllLevelIds(); // Ensure IDs are in sync
    // A v1 course has no terrain to paint, so a terrain mode left over from a v2 course would leave the
    // grid showing a matrix the file cannot carry.
    if (!$d18200295c6c5d42$var$isV2()) $d18200295c6c5d42$var$editMode = 'tiles';
    $d18200295c6c5d42$var$renderCourseInfo();
    $d18200295c6c5d42$var$renderTabs();
    $d18200295c6c5d42$var$renderLevelForm();
    $d18200295c6c5d42$var$renderPaletteMode();
    $d18200295c6c5d42$var$renderGrid();
    $d18200295c6c5d42$var$updateInfo();
    $d18200295c6c5d42$var$renderValidation();
    $d18200295c6c5d42$var$updateCourseDataOutput();
}
function $d18200295c6c5d42$var$updateAllLevelIds() {
    $d18200295c6c5d42$var$courseData.levels.forEach((level, idx)=>{
        level.id = $d18200295c6c5d42$var$computeLevelId($d18200295c6c5d42$var$courseData.id, idx + 1);
    });
}
function $d18200295c6c5d42$var$renderCourseInfo() {
    document.getElementById('courseId').value = $d18200295c6c5d42$var$courseData.id || '';
    document.getElementById('courseName').value = $d18200295c6c5d42$var$courseData.name || '';
    document.getElementById('courseDesc').value = $d18200295c6c5d42$var$courseData.description || '';
    document.getElementById('courseFormat').value = String($d18200295c6c5d42$var$courseFormat());
    // v2-only controls are dimmed rather than removed, so it stays obvious that the feature exists and
    // that the format switch is what unlocks it.
    document.querySelectorAll('.v2-only').forEach((group)=>{
        group.dataset.disabled = $d18200295c6c5d42$var$isV2() ? 'false' : 'true';
    });
    const wind = $d18200295c6c5d42$var$courseData.wind || {};
    document.getElementById('windSpeedMin').value = wind.speedMin ?? '';
    document.getElementById('windSpeedMax').value = wind.speedMax ?? '';
    document.getElementById('windAngleMin').value = wind.angleMin ?? '';
    document.getElementById('windAngleMax').value = wind.angleMax ?? '';
    // Achievement ID and Leaderboard ID are assigned outside the editor and are not editable here. A
    // course that already carries one keeps it (exportCourse round-trips whatever is on courseData), shown
    // read-only so nobody mistakes it for something the editor manages.
    const achieveInput = document.getElementById('courseAchieve');
    const achieveGroup = document.getElementById('courseAchieveGroup');
    achieveInput.value = $d18200295c6c5d42$var$courseData.finishAchievementId || '';
    achieveGroup.style.display = $d18200295c6c5d42$var$courseData.finishAchievementId ? 'flex' : 'none';
    const leaderInput = document.getElementById('courseLeader');
    const leaderGroup = document.getElementById('courseLeaderGroup');
    leaderInput.value = $d18200295c6c5d42$var$courseData.leaderboardId || '';
    leaderGroup.style.display = $d18200295c6c5d42$var$courseData.leaderboardId ? 'flex' : 'none';
}
function $d18200295c6c5d42$var$renderLevelInfo() {
    if ($d18200295c6c5d42$var$courseData.levels.length === 0) return;
    const level = $d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex];
    document.getElementById('levelId').value = level.id || '';
    document.getElementById('levelName').value = level.name || '';
    document.getElementById('levelPar').value = level.par || 3;
    const clubSelect = document.getElementById('levelClub');
    clubSelect.value = level.club || '';
    const angleInput = document.getElementById('levelAngle');
    angleInput.value = level.angle !== undefined ? level.angle : '';
}
function $d18200295c6c5d42$var$renderTabs() {
    const container = document.getElementById('holeTabs');
    container.innerHTML = '';
    $d18200295c6c5d42$var$courseData.levels.forEach((level, index)=>{
        const tab = document.createElement('div');
        tab.className = `tab ${index === $d18200295c6c5d42$var$currentLevelIndex ? 'active' : ''}`;
        tab.textContent = `Hole ${index + 1}`;
        tab.onclick = ()=>$d18200295c6c5d42$var$switchLevel(index);
        container.appendChild(tab);
    });
}
function $d18200295c6c5d42$var$renderLevelForm() {
    const isLevelSelected = $d18200295c6c5d42$var$currentLevelIndex >= 0 && $d18200295c6c5d42$var$currentLevelIndex < $d18200295c6c5d42$var$courseData.levels.length;
    // Disable inputs if no level
    const inputs = [
        'levelName',
        'levelPar',
        'levelClub',
        'levelAngle'
    ];
    inputs.forEach((id)=>{
        document.getElementById(id).disabled = !isLevelSelected;
    });
    if (isLevelSelected) {
        const level = $d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex];
        document.getElementById('levelId').value = level.id || $d18200295c6c5d42$var$computeLevelId($d18200295c6c5d42$var$courseData.id, $d18200295c6c5d42$var$currentLevelIndex + 1);
        document.getElementById('levelName').value = level.name || '';
        document.getElementById('levelPar').value = level.par || 3;
        document.getElementById('levelClub').value = level.club || '';
        document.getElementById('levelAngle').value = level.angle !== undefined ? level.angle : '';
    } else {
        document.getElementById('levelId').value = '';
        document.getElementById('levelName').value = '';
        document.getElementById('levelPar').value = '';
        document.getElementById('levelClub').value = '';
        document.getElementById('levelAngle').value = '';
    }
}
function $d18200295c6c5d42$var$renderGrid() {
    const container = document.getElementById('gridContainer');
    container.innerHTML = '';
    if ($d18200295c6c5d42$var$currentLevelIndex < 0 || !$d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex]) {
        container.innerHTML = '<p style="padding: 20px; color: #999;">No level selected. Click "+ Add Hole" to start.</p>';
        return;
    }
    const level = $d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex];
    let grid = level.grid || (0, $628bcc8876065384$export$5944653400dd6092)();
    // Ensure grid is strings map representation
    if (grid.length > 0 && typeof grid[0] === 'string') ;
    else {
        grid = (0, $628bcc8876065384$export$5944653400dd6092)();
        level.grid = grid;
    }
    // Pruned rather than read raw, so the flags drawn on the grid are exactly the ones the file will
    // carry: painting the cup onto a cell that was an alternate makes it the cup, not both.
    const pins = $d18200295c6c5d42$var$isV2() ? $d18200295c6c5d42$var$levelPins(level) : [];
    const paintingTerrain = $d18200295c6c5d42$var$editMode === 'terrain' && $d18200295c6c5d42$var$isV2();
    // Only shaped when it is being shown, so a v1 hole never grows a height matrix just by being looked
    // at — a course only carries one once somebody paints one.
    const elevation = paintingTerrain ? $d18200295c6c5d42$var$levelElevation(level) : null;
    const elevationSpan = paintingTerrain ? (0, $f135f857aa650103$export$1c941da6afed58dd)(elevation) : null;
    // A live marquee while `select` is being dragged, distinct from the rectangle already committed —
    // the two can differ for as long as the mouse is down.
    const marquee = $d18200295c6c5d42$var$currentTool === 'select' && $d18200295c6c5d42$var$dragAnchor && $d18200295c6c5d42$var$dragCurrent ? $d18200295c6c5d42$var$normaliseRect($d18200295c6c5d42$var$dragAnchor, $d18200295c6c5d42$var$dragCurrent) : null;
    // The `hill` tool previews its own radius the same way, but as a circle rather than a rectangle, and
    // with a per-cell strength so the falloff that will be applied is visible before it is committed.
    const hillPreview = $d18200295c6c5d42$var$currentTool === 'hill' && $d18200295c6c5d42$var$dragAnchor && $d18200295c6c5d42$var$dragCurrent ? {
        anchor: $d18200295c6c5d42$var$dragAnchor,
        radius: Math.max(1, Math.hypot($d18200295c6c5d42$var$dragCurrent.x - $d18200295c6c5d42$var$dragAnchor.x, $d18200295c6c5d42$var$dragCurrent.y - $d18200295c6c5d42$var$dragAnchor.y))
    } : null;
    const columns = (0, $628bcc8876065384$export$2f192c6eccb63fc0)(grid);
    grid.forEach((rowString, y)=>{
        const rowDiv = document.createElement('div');
        rowDiv.className = 'grid-row';
        for(let x = 0; x < columns; x++){
            const char = rowString.charAt(x) || 'R';
            const tileType = Object.keys((0, $8d78eed0340873cb$export$34ddeca23ac38a20)).find((key)=>(0, $8d78eed0340873cb$export$34ddeca23ac38a20)[key].mapChar === char) || 'ROUGH';
            const tile = (0, $8d78eed0340873cb$export$34ddeca23ac38a20)[tileType];
            const cell = document.createElement('div');
            cell.className = 'grid-cell';
            cell.dataset.x = x;
            cell.dataset.y = y;
            cell.style.backgroundColor = tile.color;
            if (paintingTerrain) $d18200295c6c5d42$var$decorateTerrainCell(cell, elevation, elevationSpan, x, y);
            else $d18200295c6c5d42$var$decorateTileCell(cell, tile);
            // Drawn in both modes: an author sculpting a green needs to see which cells the flag can end
            // up on, since that is most of what the sculpting is for.
            if ($d18200295c6c5d42$var$isV2() && (0, $45c5256a35480592$export$dc0d615537fa77c7)(pins, x, y)) {
                cell.classList.add('cell-pin');
                cell.textContent = "\u26F3";
                cell.title = `Alternate pin (${x}, ${y})`;
            }
            if ($d18200295c6c5d42$var$selection && x >= $d18200295c6c5d42$var$selection.x0 && x <= $d18200295c6c5d42$var$selection.x1 && y >= $d18200295c6c5d42$var$selection.y0 && y <= $d18200295c6c5d42$var$selection.y1) cell.classList.add('cell-selected');
            if (marquee && x >= marquee.x0 && x <= marquee.x1 && y >= marquee.y0 && y <= marquee.y1) cell.classList.add('cell-marquee');
            if (hillPreview) {
                const dist = Math.hypot(x - hillPreview.anchor.x, y - hillPreview.anchor.y);
                if (dist <= hillPreview.radius) {
                    const falloff = $d18200295c6c5d42$var$hillFalloff(dist, hillPreview.radius);
                    cell.classList.add('cell-hill-preview');
                    cell.style.setProperty('--hill-opacity', falloff.toFixed(2));
                }
            }
            cell.onmousedown = (e)=>{
                e.preventDefault();
                $d18200295c6c5d42$var$handleCellDown(x, y);
            };
            cell.onmouseenter = (e)=>{
                if (e.buttons === 1) $d18200295c6c5d42$var$handleCellDrag(x, y);
            };
            rowDiv.appendChild(cell);
        }
        container.appendChild(rowDiv);
    });
    $d18200295c6c5d42$var$updateInfo();
    $d18200295c6c5d42$var$renderTerrainSummary();
    $d18200295c6c5d42$var$updateSelectionControls();
}
function $d18200295c6c5d42$var$decorateTileCell(cell, tile) {
    if (tile.editorIcon) cell.textContent = tile.editorIcon;
    else if (tile.mapChar === '0') cell.textContent = "\u26F3";
    else if (tile.mapChar !== 'R' && tile.mapChar !== 'F' && tile.mapChar !== 'G' && tile.mapChar !== 'S' && tile.mapChar !== 'W' && tile.mapChar !== 'O') // For visual clarity if no icon
    cell.textContent = tile.mapChar;
}
/**
 * A cell in terrain mode: shaded by height, labelled with it, and carrying the downhill arrow.
 *
 * Every cell has a height here, unlike the green contour this replaced — there is no inert ground,
 * because all of it is ground, and the green simply takes its shape from whatever it sits on.
 */ function $d18200295c6c5d42$var$decorateTerrainCell(cell, elevation, range, x, y) {
    const height = (0, $f135f857aa650103$export$c3084f1cf8af9a05)(elevation, x, y);
    cell.classList.add('height-cell');
    // -1 at the lowest point on the hole, +1 at the highest. A hole with no variation yet shades flat,
    // which is honest: there is nothing to see until something differs.
    const span = range ? range.high - range.low : 0;
    const relief = span > 0 ? (height - range.low) / span * 2 - 1 : 0;
    const overlay = relief >= 0 ? `rgba(255, 255, 255, ${(relief * 0.5).toFixed(3)})` : `rgba(0, 0, 0, ${(-relief * 0.45).toFixed(3)})`;
    cell.style.backgroundImage = `linear-gradient(${overlay}, ${overlay})`;
    const label = document.createElement('span');
    label.textContent = String(height);
    cell.appendChild(label);
    const slope = (0, $f135f857aa650103$export$21a9d435687815aa)(elevation, x, y);
    if (slope.steepness >= 0.3) {
        const arrow = document.createElement('span');
        arrow.className = 'height-arrow';
        // The glyph points north and the angle is clockwise from north, which is the rotation directly.
        arrow.textContent = "\u2191";
        arrow.style.transform = `rotate(${slope.angle.toFixed(1)}deg)`;
        arrow.title = `${(0, $490d5ef8e3640c94$export$7919ba25f6145df6)(slope.steepness)} \u{2014} falls towards ${Math.round(slope.angle)}\xb0`;
        cell.appendChild(arrow);
    }
}
/**
 * The hole's terrain matrix, created and shaped to the grid on first use.
 *
 * A new one starts at the middle of the range rather than at zero, so an author's first stroke can go
 * either up or down from the ground they are standing on. Starting at 0 would mean every hole could only
 * ever be sculpted upwards.
 */ function $d18200295c6c5d42$var$levelElevation(level) {
    level.elevation = (0, $f135f857aa650103$export$3fbf83d1255045e9)(level.elevation, level.grid, (0, $f135f857aa650103$export$84cd9b1ff2925cc4));
    return level.elevation;
}
function $d18200295c6c5d42$var$renderTerrainSummary() {
    const summary = document.getElementById('terrainSummary');
    if (!summary) return;
    if (!$d18200295c6c5d42$var$isHeightMode() || $d18200295c6c5d42$var$currentLevelIndex < 0) {
        summary.textContent = '';
        return;
    }
    const level = $d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex];
    const range = (0, $f135f857aa650103$export$1c941da6afed58dd)($d18200295c6c5d42$var$levelElevation(level));
    if (!range || range.low === range.high) {
        summary.textContent = `Hole is flat at ${range ? range.low : (0, $f135f857aa650103$export$84cd9b1ff2925cc4)}. A flat hole carries no terrain in the file.`;
        return;
    }
    // The number an author is actually designing against, in the units the game's CLIMB readout uses.
    const drop = range.high - range.low;
    const extent = `Ground runs ${range.low}\u{2013}${range.high} \u{2014} ${drop} step(s), ${drop * 2} m.`;
    const tee = $d18200295c6c5d42$var$teeCell(level);
    const cup = $d18200295c6c5d42$var$cupCell(level);
    if (!tee || !cup) {
        // Without both there is nothing to measure a climb between, and reporting one anyway would
        // mean quietly measuring from the top-left corner.
        summary.textContent = `${extent} Paint a tee and a cup to see how the hole plays.`;
        return;
    }
    const climb = (0, $f135f857aa650103$export$d531e3ecc3c8ca83)(level.elevation, tee, cup);
    const played = climb === 0 ? 'The tee and the cup are level.' : `The hole plays ${Math.abs(climb)} m ${climb > 0 ? 'uphill' : 'downhill'}.`;
    summary.textContent = `${extent} ${played}`;
}
/** Where the tee is, read from the grid the way the game reads it: the first `X`. `null` if unpainted. */ function $d18200295c6c5d42$var$teeCell(level) {
    return $d18200295c6c5d42$var$findChar(level.grid || [], 'X');
}
/** Where the cup is: the first `0`, likewise. */ function $d18200295c6c5d42$var$cupCell(level) {
    return $d18200295c6c5d42$var$findChar(level.grid || [], '0');
}
function $d18200295c6c5d42$var$findChar(grid, char) {
    for(let y = 0; y < grid.length; y++){
        const x = grid[y].indexOf(char);
        if (x >= 0) return {
            x: x,
            y: y
        };
    }
    return null;
}
function $d18200295c6c5d42$var$updateInfo() {
    if ($d18200295c6c5d42$var$currentLevelIndex >= 0 && $d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex] && $d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex].grid) {
        const grid = $d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex].grid;
        document.getElementById('levelSize').textContent = `${(0, $628bcc8876065384$export$2f192c6eccb63fc0)(grid)} x ${grid.length}`;
    } else document.getElementById('levelSize').textContent = `${0, $628bcc8876065384$export$aa4eace044cdfdbf} x 0`;
    // Counted the way the game counts: the painted cup plus its alternates. "1" is a hole that plays the
    // same every round, which is what every hole was before this existed.
    const pins = document.getElementById('levelPins');
    if (!pins) return;
    if ($d18200295c6c5d42$var$currentLevelIndex < 0 || !$d18200295c6c5d42$var$isV2()) {
        pins.textContent = $d18200295c6c5d42$var$isV2() ? '1' : '1 (v2 only)';
        return;
    }
    const count = 1 + $d18200295c6c5d42$var$levelPins($d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex]).length;
    pins.textContent = count === 1 ? "1 \u2014 cut the same way every round" : `${count} \u{2014} one is drawn each round`;
}
function $d18200295c6c5d42$var$updateCourseDataOutput() {
    // We don't need this to be visible to the user anymore, but keeping it updated in case we want to debug
    const jsonOutput = JSON.stringify($d18200295c6c5d42$var$courseData, null, 4);
    document.getElementById('courseDataOutput').value = jsonOutput;
}
// --- UI Interaction Handlers ---
function $d18200295c6c5d42$var$initTileSelector() {
    const selector = document.getElementById('tileSelector');
    selector.innerHTML = '';
    Object.keys((0, $8d78eed0340873cb$export$34ddeca23ac38a20)).forEach((tileType)=>{
        const tile = (0, $8d78eed0340873cb$export$34ddeca23ac38a20)[tileType];
        const button = document.createElement('div');
        button.className = 'tile-button';
        if (tileType === $d18200295c6c5d42$var$selectedTileType) button.classList.add('selected');
        button.style.backgroundColor = tile.color;
        if (tile.editorIcon) button.textContent = tile.editorIcon;
        else {
            button.textContent = tile.mapChar;
            button.style.fontSize = '20px';
            button.style.fontWeight = 'bold';
            button.style.color = tile.color === '#555555' ? '#fff' : '#333';
        }
        const label = document.createElement('div');
        label.className = 'label';
        label.textContent = tile.mapTitle;
        button.appendChild(label);
        button.onclick = ()=>$d18200295c6c5d42$var$selectTile(tileType);
        selector.appendChild(button);
    });
}
function $d18200295c6c5d42$var$selectTile(tileType) {
    $d18200295c6c5d42$var$selectedTileType = tileType;
    document.getElementById('selectedTile').textContent = (0, $8d78eed0340873cb$export$34ddeca23ac38a20)[tileType].mapTitle;
    $d18200295c6c5d42$var$initTileSelector(); // Re-render to show selected border
}
function $d18200295c6c5d42$var$updateCourseData() {
    $d18200295c6c5d42$var$courseData.id = document.getElementById('courseId').value;
    $d18200295c6c5d42$var$courseData.name = document.getElementById('courseName').value;
    $d18200295c6c5d42$var$courseData.description = document.getElementById('courseDesc').value;
    // Wind. Dropped entirely when there is no speed to blow, so a calm course carries no wind entry
    // rather than a zeroed one — the game treats both as calm, and a file should not claim a feature it
    // does not use.
    const windSpeedMin = parseFloat(document.getElementById('windSpeedMin').value);
    const windSpeedMax = parseFloat(document.getElementById('windSpeedMax').value);
    const windAngleMin = parseFloat(document.getElementById('windAngleMin').value);
    const windAngleMax = parseFloat(document.getElementById('windAngleMax').value);
    const topSpeed = Number.isFinite(windSpeedMax) ? windSpeedMax : windSpeedMin;
    // Only on a v2 course. The format switch owns the version now, rather than wind quietly bumping it:
    // with sloped greens in the format too, "which features may I use" is a decision the author should
    // make once and see, not something that happens as a side effect of typing in a box.
    if ($d18200295c6c5d42$var$isV2() && Number.isFinite(topSpeed) && topSpeed > 0) $d18200295c6c5d42$var$courseData.wind = {
        speedMin: Number.isFinite(windSpeedMin) ? windSpeedMin : 0,
        speedMax: topSpeed,
        angleMin: Number.isFinite(windAngleMin) ? windAngleMin : 0,
        angleMax: Number.isFinite(windAngleMax) ? windAngleMax : 359
    };
    else delete $d18200295c6c5d42$var$courseData.wind;
    $d18200295c6c5d42$var$updateAllLevelIds(); // Update level IDs immediately when Course ID changes
    $d18200295c6c5d42$var$renderLevelForm(); // Re-render to show updated level ID
    $d18200295c6c5d42$var$saveState();
}
function $d18200295c6c5d42$var$updateLevelData() {
    if ($d18200295c6c5d42$var$currentLevelIndex < 0) return;
    const level = $d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex];
    level.name = document.getElementById('levelName').value;
    const parVal = parseInt(document.getElementById('levelPar').value);
    if (!isNaN(parVal)) level.par = parVal;
    const club = document.getElementById('levelClub').value;
    if (club) level.club = club;
    else delete level.club;
    const angle = document.getElementById('levelAngle').value;
    if (angle && !isNaN(parseInt(angle))) level.angle = parseInt(angle);
    else delete level.angle;
    $d18200295c6c5d42$var$saveState();
}
function $d18200295c6c5d42$var$switchLevel(index) {
    if (index >= 0 && index < $d18200295c6c5d42$var$courseData.levels.length) $d18200295c6c5d42$var$currentLevelIndex = index;
    else $d18200295c6c5d42$var$currentLevelIndex = -1;
    // A selection is a pair of grid coordinates; nothing says they still make sense against a different
    // hole's grid, which can be a different height.
    $d18200295c6c5d42$var$clearSelection();
    $d18200295c6c5d42$var$renderTabs();
    $d18200295c6c5d42$var$renderLevelForm();
    $d18200295c6c5d42$var$renderGrid();
    $d18200295c6c5d42$var$updateInfo();
}
function $d18200295c6c5d42$var$addLevel() {
    const numLevels = $d18200295c6c5d42$var$courseData.levels.length;
    const newLevel = {
        name: `Hole ${numLevels + 1}`,
        par: 3,
        grid: (0, $628bcc8876065384$export$5944653400dd6092)()
    };
    $d18200295c6c5d42$var$courseData.levels.push(newLevel);
    $d18200295c6c5d42$var$currentLevelIndex = $d18200295c6c5d42$var$courseData.levels.length - 1;
    $d18200295c6c5d42$var$clearSelection();
    $d18200295c6c5d42$var$updateAllLevelIds();
    $d18200295c6c5d42$var$renderAll();
    $d18200295c6c5d42$var$saveState();
}
function $d18200295c6c5d42$var$deleteLevel() {
    if ($d18200295c6c5d42$var$currentLevelIndex < 0) return;
    if (confirm('Are you sure you want to delete this hole?')) {
        $d18200295c6c5d42$var$courseData.levels.splice($d18200295c6c5d42$var$currentLevelIndex, 1);
        $d18200295c6c5d42$var$currentLevelIndex = $d18200295c6c5d42$var$courseData.levels.length > 0 ? Math.min($d18200295c6c5d42$var$currentLevelIndex, $d18200295c6c5d42$var$courseData.levels.length - 1) : -1;
        $d18200295c6c5d42$var$clearSelection();
        $d18200295c6c5d42$var$updateAllLevelIds();
        $d18200295c6c5d42$var$renderAll();
        $d18200295c6c5d42$var$saveState();
    }
}
// --- Painting tools ---
//
// `currentTool` picks the gesture; every tool still writes through the same two matrices `paintCell`
// always did (the tile grid, or the terrain elevation in v2's terrain mode). A mousedown on a cell goes
// through `handleCellDown`, a drag while the button is held through `handleCellDrag`, and `select`/`hill`
// both defer their actual write to mouseup (see `init`) so the rectangle or radius used is the one the
// author finished dragging, not whatever it was mid-gesture.
function $d18200295c6c5d42$var$handleCellDown(x, y) {
    if ($d18200295c6c5d42$var$currentLevelIndex < 0) return;
    $d18200295c6c5d42$var$isPainting = true;
    if ($d18200295c6c5d42$var$currentTool === 'pin') {
        $d18200295c6c5d42$var$togglePinAt(x, y);
        $d18200295c6c5d42$var$isPainting = false;
    } else if ($d18200295c6c5d42$var$currentTool === 'bucket') {
        $d18200295c6c5d42$var$floodFill(x, y);
        $d18200295c6c5d42$var$isPainting = false;
    } else if ($d18200295c6c5d42$var$currentTool === 'select') {
        $d18200295c6c5d42$var$dragAnchor = {
            x: x,
            y: y
        };
        $d18200295c6c5d42$var$dragCurrent = {
            x: x,
            y: y
        };
        $d18200295c6c5d42$var$selection = null;
        $d18200295c6c5d42$var$renderGrid();
    } else if ($d18200295c6c5d42$var$currentTool === 'hill') {
        if (!($d18200295c6c5d42$var$editMode === 'terrain' && $d18200295c6c5d42$var$isV2())) return;
        $d18200295c6c5d42$var$dragAnchor = {
            x: x,
            y: y
        };
        $d18200295c6c5d42$var$dragCurrent = {
            x: x,
            y: y
        };
        $d18200295c6c5d42$var$renderGrid();
    } else $d18200295c6c5d42$var$paintCell(x, y);
}
function $d18200295c6c5d42$var$handleCellDrag(x, y) {
    if (!$d18200295c6c5d42$var$isPainting) return;
    if ($d18200295c6c5d42$var$currentTool === 'brush') $d18200295c6c5d42$var$paintCell(x, y);
    else if ($d18200295c6c5d42$var$currentTool === 'select' || $d18200295c6c5d42$var$currentTool === 'hill') {
        $d18200295c6c5d42$var$dragCurrent = {
            x: x,
            y: y
        };
        $d18200295c6c5d42$var$renderGrid();
    }
}
function $d18200295c6c5d42$var$paintCell(x, y) {
    if ($d18200295c6c5d42$var$currentLevelIndex < 0) return;
    const level = $d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex];
    if ($d18200295c6c5d42$var$editMode === 'terrain' && $d18200295c6c5d42$var$isV2()) {
        // Every cell is ground, so there is nowhere painting is a no-op.
        const elevation = $d18200295c6c5d42$var$levelElevation(level);
        const rowChars = elevation[y].split('');
        rowChars[x] = String((0, $490d5ef8e3640c94$export$4465cd453867bc23)($d18200295c6c5d42$var$selectedHeight));
        elevation[y] = rowChars.join('');
    } else {
        // Strings are immutable in JS, convert row to array, modify, join back
        let rowChars = level.grid[y].split('');
        rowChars[x] = (0, $8d78eed0340873cb$export$34ddeca23ac38a20)[$d18200295c6c5d42$var$selectedTileType].mapChar;
        level.grid[y] = rowChars.join('');
        $d18200295c6c5d42$var$prunePins(level);
    }
    $d18200295c6c5d42$var$renderGrid(); // Fast enough for small grids
    $d18200295c6c5d42$var$renderValidation();
    $d18200295c6c5d42$var$updateCourseDataOutput();
// Note: saveState() is handled on mouseup to avoid thrashing localStorage on drag
}
/**
 * Add or remove one of this hole's alternate cups.
 *
 * The cup's own cell is refused rather than toggled: it is already where the flag stands by default, so
 * "adding" it would do nothing and removing it would suggest the hole could have no cup at all. Painting
 * over the `0` is how you move the default one.
 */ function $d18200295c6c5d42$var$togglePinAt(x, y) {
    if ($d18200295c6c5d42$var$currentLevelIndex < 0) return;
    const level = $d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex];
    if (!$d18200295c6c5d42$var$isV2()) {
        alert('Alternate pins are a v2 feature. Switch the course format to v2 first.');
        return;
    }
    if ((level.grid[y] || '').charAt(x) === '0') {
        alert('That is the hole\u2019s own cup. Alternate pins are the other places it may be cut to.');
        return;
    }
    const pins = (0, $45c5256a35480592$export$14f2f3b8de00530c)(level.pinPositions, x, y);
    if (pins.length > 0) level.pinPositions = pins;
    else delete level.pinPositions;
    $d18200295c6c5d42$var$renderGrid();
    $d18200295c6c5d42$var$renderValidation();
    $d18200295c6c5d42$var$updateCourseDataOutput();
    $d18200295c6c5d42$var$saveState();
}
/** Every alternate this hole currently declares, shaped against its grid. */ function $d18200295c6c5d42$var$levelPins(level) {
    return (0, $45c5256a35480592$export$7904b8e4f0b7816f)(level && level.pinPositions, level && level.grid || []);
}
/**
 * Drop any alternate the grid has just made meaningless, after a tile write.
 *
 * The one case that actually happens: an author moves the cup onto a cell that was an alternate. It is
 * now *the* cup, and leaving it in the list would draw a flag on it that the file will not contain — or
 * worse, bring it back as an alternate the moment the cup moves on again.
 */ function $d18200295c6c5d42$var$prunePins(level) {
    if (!level || !level.pinPositions) return;
    const pins = $d18200295c6c5d42$var$levelPins(level);
    if (pins.length > 0) level.pinPositions = pins;
    else delete level.pinPositions;
}
/**
 * Every cell 4-connected to `(startX, startY)` that matches it, in tiles mode by tile character and in
 * terrain mode by elevation step — the classic paint-bucket fill. Iterative rather than recursive so a
 * fill that reaches every cell of a large hole cannot blow the call stack.
 */ function $d18200295c6c5d42$var$floodFill(x, y) {
    if ($d18200295c6c5d42$var$currentLevelIndex < 0) return;
    const level = $d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex];
    const rows = level.grid.length;
    const columns = (0, $628bcc8876065384$export$2f192c6eccb63fc0)(level.grid);
    if ($d18200295c6c5d42$var$editMode === 'terrain' && $d18200295c6c5d42$var$isV2()) {
        const elevation = $d18200295c6c5d42$var$levelElevation(level);
        const target = (0, $f135f857aa650103$export$c3084f1cf8af9a05)(elevation, x, y);
        if (target === (0, $490d5ef8e3640c94$export$4465cd453867bc23)($d18200295c6c5d42$var$selectedHeight)) return;
        $d18200295c6c5d42$var$floodApply(rows, columns, x, y, (cx, cy)=>(0, $f135f857aa650103$export$c3084f1cf8af9a05)(elevation, cx, cy) === target, (cx, cy)=>{
            const row = elevation[cy].split('');
            row[cx] = String((0, $490d5ef8e3640c94$export$4465cd453867bc23)($d18200295c6c5d42$var$selectedHeight));
            elevation[cy] = row.join('');
        });
    } else {
        const targetChar = level.grid[y].charAt(x);
        const newChar = (0, $8d78eed0340873cb$export$34ddeca23ac38a20)[$d18200295c6c5d42$var$selectedTileType].mapChar;
        if (targetChar === newChar) return;
        $d18200295c6c5d42$var$floodApply(rows, columns, x, y, (cx, cy)=>level.grid[cy].charAt(cx) === targetChar, (cx, cy)=>{
            const row = level.grid[cy].split('');
            row[cx] = newChar;
            level.grid[cy] = row.join('');
        });
        $d18200295c6c5d42$var$prunePins(level);
    }
    $d18200295c6c5d42$var$renderGrid();
    $d18200295c6c5d42$var$renderValidation();
    $d18200295c6c5d42$var$saveState();
}
function $d18200295c6c5d42$var$floodApply(rows, columns, startX, startY, matches, apply) {
    const visited = new Set();
    const stack = [
        [
            startX,
            startY
        ]
    ];
    while(stack.length > 0){
        const [cx, cy] = stack.pop();
        if (cx < 0 || cx >= columns || cy < 0 || cy >= rows) continue;
        const key = cy * columns + cx;
        if (visited.has(key)) continue;
        visited.add(key);
        if (!matches(cx, cy)) continue;
        apply(cx, cy);
        stack.push([
            cx + 1,
            cy
        ], [
            cx - 1,
            cy
        ], [
            cx,
            cy + 1
        ], [
            cx,
            cy - 1
        ]);
    }
}
/** How strongly the `hill` tool's falloff applies at `dist` from its centre, within `radius`: 1 at the
 *  centre easing smoothly to 0 at the edge, so the result blends into the surrounding terrain rather than
 *  stopping at a hard edge. */ function $d18200295c6c5d42$var$hillFalloff(dist, radius) {
    const t = Math.min(1, dist / radius);
    return (Math.cos(t * Math.PI) + 1) / 2;
}
/**
 * Sculpts a smooth rise or dip: every cell within `radius` of `anchor` blends towards `selectedHeight`,
 * strongest at the centre and fading to no change at all at the edge. Dragging further out (a bigger
 * `radius`) spreads the same change over more ground rather than making it more extreme, which is what
 * makes this a hill/dig tool rather than just a big brush.
 */ function $d18200295c6c5d42$var$commitHillDig(anchor, current) {
    if ($d18200295c6c5d42$var$currentLevelIndex < 0 || !($d18200295c6c5d42$var$editMode === 'terrain' && $d18200295c6c5d42$var$isV2())) return;
    const level = $d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex];
    const radius = Math.max(1, Math.hypot(current.x - anchor.x, current.y - anchor.y));
    const elevation = $d18200295c6c5d42$var$levelElevation(level);
    const rows = elevation.length;
    const minY = Math.max(0, Math.floor(anchor.y - radius));
    const maxY = Math.min(rows - 1, Math.ceil(anchor.y + radius));
    const minX = Math.max(0, Math.floor(anchor.x - radius));
    const maxX = Math.min((0, $628bcc8876065384$export$2f192c6eccb63fc0)(level.grid) - 1, Math.ceil(anchor.x + radius));
    for(let y = minY; y <= maxY; y++){
        const row = elevation[y].split('');
        for(let x = minX; x <= maxX; x++){
            const dist = Math.hypot(x - anchor.x, y - anchor.y);
            if (dist > radius) continue;
            const falloff = $d18200295c6c5d42$var$hillFalloff(dist, radius);
            const original = parseInt(row[x], 10);
            const blended = original + ($d18200295c6c5d42$var$selectedHeight - original) * falloff;
            row[x] = String((0, $490d5ef8e3640c94$export$4465cd453867bc23)(Math.round(blended)));
        }
        elevation[y] = row.join('');
    }
    $d18200295c6c5d42$var$renderGrid();
    $d18200295c6c5d42$var$renderValidation();
}
function $d18200295c6c5d42$var$setTool(tool) {
    if (tool === 'hill' && !($d18200295c6c5d42$var$editMode === 'terrain' && $d18200295c6c5d42$var$isV2())) return;
    if (tool === 'pin' && !$d18200295c6c5d42$var$isV2()) {
        alert('Alternate pins are a v2 feature. Switch the course format to v2 first.');
        return;
    }
    $d18200295c6c5d42$var$currentTool = tool;
    $d18200295c6c5d42$var$dragAnchor = null;
    $d18200295c6c5d42$var$dragCurrent = null;
    $d18200295c6c5d42$var$renderToolbar();
    $d18200295c6c5d42$var$renderGrid();
}
function $d18200295c6c5d42$var$renderToolbar() {
    const terrain = $d18200295c6c5d42$var$editMode === 'terrain' && $d18200295c6c5d42$var$isV2();
    document.getElementById('toolHill').style.display = terrain ? 'inline-block' : 'none';
    if (!terrain && $d18200295c6c5d42$var$currentTool === 'hill') $d18200295c6c5d42$var$currentTool = 'brush';
    // A v1 course cannot carry alternates at all, so the tool is not offered rather than offered and
    // refused — the same treatment Hill/Dig gets outside terrain mode.
    document.getElementById('toolPin').style.display = $d18200295c6c5d42$var$isV2() ? 'inline-block' : 'none';
    if (!$d18200295c6c5d42$var$isV2() && $d18200295c6c5d42$var$currentTool === 'pin') $d18200295c6c5d42$var$currentTool = 'brush';
    const toolButtons = {
        toolBrush: 'brush',
        toolSelect: 'select',
        toolBucket: 'bucket',
        toolHill: 'hill',
        toolPin: 'pin'
    };
    Object.entries(toolButtons).forEach(([id, tool])=>{
        document.getElementById(id).classList.toggle('active', tool === $d18200295c6c5d42$var$currentTool);
    });
}
function $d18200295c6c5d42$var$normaliseRect(a, b) {
    return {
        x0: Math.min(a.x, b.x),
        x1: Math.max(a.x, b.x),
        y0: Math.min(a.y, b.y),
        y1: Math.max(a.y, b.y)
    };
}
function $d18200295c6c5d42$var$selectionCellCount() {
    if (!$d18200295c6c5d42$var$selection) return 0;
    return ($d18200295c6c5d42$var$selection.x1 - $d18200295c6c5d42$var$selection.x0 + 1) * ($d18200295c6c5d42$var$selection.y1 - $d18200295c6c5d42$var$selection.y0 + 1);
}
function $d18200295c6c5d42$var$clearSelection() {
    $d18200295c6c5d42$var$selection = null;
    $d18200295c6c5d42$var$renderGrid();
}
function $d18200295c6c5d42$var$updateSelectionControls() {
    const clearBtn = document.getElementById('clearSelectionBtn');
    if (clearBtn) clearBtn.style.display = $d18200295c6c5d42$var$selection ? 'inline-block' : 'none';
    const fillBtn = document.getElementById('fillBtn');
    if (!fillBtn) return;
    const terrain = $d18200295c6c5d42$var$editMode === 'terrain' && $d18200295c6c5d42$var$isV2();
    if ($d18200295c6c5d42$var$selection) fillBtn.textContent = terrain ? `Fill Selected Area (${$d18200295c6c5d42$var$selectionCellCount()} cells) with Height` : `Fill Selected Area (${$d18200295c6c5d42$var$selectionCellCount()} cells) with Tile`;
    else fillBtn.textContent = terrain ? 'Fill Hole with Height' : 'Fill with Selected Tile';
}
// --- v2: format editing ---
function $d18200295c6c5d42$var$changeCourseFormat() {
    const wanted = parseInt(document.getElementById('courseFormat').value, 10) === 1 ? 1 : 2;
    if (wanted === $d18200295c6c5d42$var$courseFormat()) return;
    if (wanted === 1) {
        const losing = [];
        if ($d18200295c6c5d42$var$courseData.wind) losing.push('the wind');
        // The editor no longer authors a green contour, but a course loaded from before that change may
        // still carry one, and downgrading it to v1 would silently throw that work away.
        if ($d18200295c6c5d42$var$courseData.levels.some((level)=>(0, $490d5ef8e3640c94$export$bd477a8154a23014)(level.grid || [], level.heights || []))) losing.push('every green contour');
        if ($d18200295c6c5d42$var$courseData.levels.some((level)=>(0, $f135f857aa650103$export$afd9c060cc316883)(level.elevation || []))) losing.push('every hole\'s terrain');
        if ($d18200295c6c5d42$var$courseData.levels.some((level)=>$d18200295c6c5d42$var$levelPins(level).length > 0)) losing.push('every alternate pin');
        // Width is not in that list because it is not thrown away — the grids keep their columns. It is
        // played differently, which is worse to find out afterwards: the game reads a v1 hole at exactly
        // `DEFAULT_WIDTH`, so a wider hole loses its right-hand columns and a narrower one gains
        // out-of-bounds ones. Said as its own sentence rather than folded in with the deletions.
        const rewidening = $d18200295c6c5d42$var$courseData.levels.some((level)=>(0, $628bcc8876065384$export$2f192c6eccb63fc0)(level.grid || []) !== (0, $628bcc8876065384$export$aa4eace044cdfdbf));
        const them = losing.length > 1 ? 'them' : 'it';
        const sentences = [];
        if (losing.length > 0) sentences.push(`Switching to v1 removes ${losing.join(' and ')}. A v1 course cannot carry ${them}.`);
        if (rewidening) sentences.push(`v1 plays every hole at exactly ${(0, $628bcc8876065384$export$aa4eace044cdfdbf)} columns, so holes of any other width will be cropped or padded.`);
        if (sentences.length > 0 && !confirm(`${sentences.join(' ')} Continue?`)) {
            // Put the control back where it was; the state never moved.
            document.getElementById('courseFormat').value = String($d18200295c6c5d42$var$courseFormat());
            return;
        }
        delete $d18200295c6c5d42$var$courseData.wind;
        $d18200295c6c5d42$var$courseData.levels.forEach((level)=>{
            delete level.heights;
            delete level.elevation;
            delete level.pinPositions;
        });
        $d18200295c6c5d42$var$courseData.formatVersion = 1;
        $d18200295c6c5d42$var$editMode = 'tiles';
    } else $d18200295c6c5d42$var$courseData.formatVersion = 2;
    $d18200295c6c5d42$var$renderAll();
    $d18200295c6c5d42$var$saveState();
}
function $d18200295c6c5d42$var$setEditMode(mode) {
    if (mode === 'terrain' && !$d18200295c6c5d42$var$isV2()) {
        alert('Terrain elevation is a v2 feature. Switch the course format to v2 first.');
        return;
    }
    $d18200295c6c5d42$var$editMode = mode === 'terrain' ? 'terrain' : 'tiles';
    $d18200295c6c5d42$var$renderPaletteMode();
    $d18200295c6c5d42$var$renderGrid();
}
function $d18200295c6c5d42$var$renderPaletteMode() {
    const terrain = $d18200295c6c5d42$var$editMode === 'terrain' && $d18200295c6c5d42$var$isV2();
    document.getElementById('tileSelector').style.display = terrain ? 'none' : 'flex';
    document.getElementById('terrainPanel').style.display = terrain ? 'flex' : 'none';
    document.getElementById('terrainHelp').style.display = terrain ? 'block' : 'none';
    document.getElementById('paletteTitle').textContent = terrain ? 'Ground Height' : 'Tile Palette';
    document.getElementById('modeTiles').classList.toggle('active', !terrain);
    document.getElementById('modeTerrain').classList.toggle('active', terrain);
    $d18200295c6c5d42$var$renderToolbar();
}
function $d18200295c6c5d42$var$initHeightSelector() {
    const selector = document.getElementById('heightSelector');
    if (!selector) return;
    selector.innerHTML = '';
    for(let height = (0, $490d5ef8e3640c94$export$70d33a2740b029ae); height <= (0, $490d5ef8e3640c94$export$daa2496be4772440); height++){
        const button = document.createElement('div');
        button.className = 'height-button';
        button.textContent = String(height);
        // The same low-to-high ramp the grid shades with, so the palette reads as an elevation scale
        // rather than as ten arbitrary buttons.
        const level = height / (0, $490d5ef8e3640c94$export$daa2496be4772440);
        const shade = Math.round(40 + level * 180);
        button.style.backgroundColor = `rgb(${shade}, ${Math.round(shade * 0.85 + 30)}, ${shade})`;
        button.onclick = ()=>$d18200295c6c5d42$var$selectHeight(height);
        button.dataset.height = String(height);
        selector.appendChild(button);
    }
    $d18200295c6c5d42$var$selectHeight($d18200295c6c5d42$var$selectedHeight);
}
function $d18200295c6c5d42$var$selectHeight(height) {
    $d18200295c6c5d42$var$selectedHeight = (0, $490d5ef8e3640c94$export$4465cd453867bc23)(height);
    document.querySelectorAll('.height-button').forEach((button)=>{
        button.classList.toggle('selected', parseInt(button.dataset.height, 10) === $d18200295c6c5d42$var$selectedHeight);
    });
}
function $d18200295c6c5d42$var$clearTerrain() {
    if ($d18200295c6c5d42$var$currentLevelIndex < 0) return;
    const level = $d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex];
    if (!confirm('Flatten this hole? Its terrain will be removed from the file.')) return;
    delete level.elevation;
    $d18200295c6c5d42$var$renderGrid();
    $d18200295c6c5d42$var$renderValidation();
    $d18200295c6c5d42$var$saveState();
}
/** Whether the Course Problems panel is collapsed. Not persisted — a course with problems should
 *  open expanded on reload rather than hide them because of a stale choice. */ let $d18200295c6c5d42$var$validationCollapsed = false;
function $d18200295c6c5d42$var$toggleValidation() {
    $d18200295c6c5d42$var$validationCollapsed = !$d18200295c6c5d42$var$validationCollapsed;
    document.getElementById('validationPanel')?.classList.toggle('collapsed', $d18200295c6c5d42$var$validationCollapsed);
}
function $d18200295c6c5d42$var$renderValidation() {
    const panel = document.getElementById('validationPanel');
    const title = document.getElementById('validationTitle');
    const body = document.getElementById('validationBody');
    if (!panel || !title || !body) return;
    panel.classList.toggle('collapsed', $d18200295c6c5d42$var$validationCollapsed);
    const warnings = (0, $54c87622d515a545$export$45b76f4d03ea4105)($d18200295c6c5d42$var$courseData);
    if (warnings.length === 0) {
        panel.classList.remove('has-problems');
        panel.classList.add('ok');
        title.textContent = `\u{2713} Course Problems \u{2014} ${$d18200295c6c5d42$var$courseData.levels.length} hole(s), no problems found.`;
        body.innerHTML = '';
        return;
    }
    panel.classList.remove('ok');
    panel.classList.add('has-problems');
    title.textContent = `Course Problems (${warnings.length})`;
    body.innerHTML = '<ul>' + warnings.map((warning)=>`<li>${warning}</li>`).join('') + '</ul>';
}
/**
 * Resize the hole.
 *
 * Height has always been per-hole; width is new, and the two are asked for together because they are one
 * question — "how big is this hole" — and answering it in two dialogs invites an author to change one and
 * forget the other.
 *
 * Width is v2-only, for the same reason the terrain and the pins are: the game plays *every* v1 hole at
 * exactly `DEFAULT_WIDTH`, padding a short row and discarding anything past it, so a 30-wide v1 hole
 * would be a hole the editor draws one way and the game plays another. A v1 course is told so and keeps
 * its height dialog.
 *
 * Growing is free. Shrinking throws away whatever was painted in the columns and rows that go, which is
 * why it asks first — and why the caller of `resizedGrid` is the one holding the confirmation, not
 * `resizedGrid` itself.
 */ function $d18200295c6c5d42$var$changeGridSize() {
    if ($d18200295c6c5d42$var$currentLevelIndex < 0) return;
    const level = $d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex];
    const currentWidth = (0, $628bcc8876065384$export$2f192c6eccb63fc0)(level.grid);
    const currentHeight = level.grid.length;
    let newWidth = currentWidth;
    if ($d18200295c6c5d42$var$isV2()) {
        const answer = prompt(`Width in tiles (${(0, $628bcc8876065384$export$fcb4e206d7a8f717).min}\u2013${(0, $628bcc8876065384$export$fcb4e206d7a8f717).max}):`, currentWidth);
        if (answer === null) return;
        if (answer.trim() === '' || isNaN(answer)) return;
        newWidth = (0, $628bcc8876065384$export$e9f3dfb19daa974)(parseInt(answer, 10), (0, $628bcc8876065384$export$fcb4e206d7a8f717));
    }
    const answer = prompt(`Height in rows (${(0, $628bcc8876065384$export$dcbc037c875a37c7).min}\u2013${(0, $628bcc8876065384$export$dcbc037c875a37c7).max}):`, currentHeight);
    if (answer === null) return;
    if (answer.trim() === '' || isNaN(answer)) return;
    const newHeight = (0, $628bcc8876065384$export$e9f3dfb19daa974)(parseInt(answer, 10), (0, $628bcc8876065384$export$dcbc037c875a37c7));
    if (newWidth === currentWidth && newHeight === currentHeight) return;
    // Named so the author can see which way each axis went before agreeing to lose anything.
    const losing = [];
    if (newWidth < currentWidth) losing.push(`${currentWidth - newWidth} column(s) from the right`);
    if (newHeight < currentHeight) losing.push(`${currentHeight - newHeight} row(s) from the bottom`);
    if (losing.length > 0 && !confirm(`This crops ${losing.join(' and ')}. Continue?`)) return;
    level.grid = (0, $628bcc8876065384$export$67f805715e25b698)(level.grid, newWidth, newHeight);
    // Both matrices are parallel to the grid, so they have to follow it in both directions — a matrix a
    // column short would read as a step change down the new edge rather than as ground continuing.
    // Only a legacy course still carries `heights`; the editor no longer authors one.
    if (level.heights) level.heights = (0, $490d5ef8e3640c94$export$cc650f51529c4068)(level.heights, level.grid);
    if (level.elevation) level.elevation = (0, $f135f857aa650103$export$3fbf83d1255045e9)(level.elevation, level.grid);
    // A pin outside the new bounds is a flag off the end of the hole. Dropped rather than clamped: there
    // is no cell it obviously belongs on instead, and a cup silently relocated is worse than one the
    // author has to place again.
    if (level.pinPositions) {
        const pins = (0, $45c5256a35480592$export$7904b8e4f0b7816f)(level.pinPositions, level.grid);
        if (pins.length > 0) level.pinPositions = pins;
        else delete level.pinPositions;
    }
    // A selection made against the old size may no longer fit the new one.
    $d18200295c6c5d42$var$clearSelection();
    $d18200295c6c5d42$var$renderGrid();
    $d18200295c6c5d42$var$renderValidation();
    $d18200295c6c5d42$var$updateCourseDataOutput();
    $d18200295c6c5d42$var$saveState();
}
/**
 * Fills either the whole grid, or just the selected rectangle when `select` has one active — the same
 * button does both, since "fill" means the same thing either way and a selection is just a smaller target.
 */ function $d18200295c6c5d42$var$fillGrid() {
    if ($d18200295c6c5d42$var$currentLevelIndex < 0) return;
    const level = $d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex];
    const areaLabel = $d18200295c6c5d42$var$selection ? `the selected area (${$d18200295c6c5d42$var$selectionCellCount()} cells)` : 'the entire grid';
    if ($d18200295c6c5d42$var$editMode === 'terrain' && $d18200295c6c5d42$var$isV2()) {
        if (!confirm(`Set ${areaLabel} to height ${$d18200295c6c5d42$var$selectedHeight}?`)) return;
        const elevation = $d18200295c6c5d42$var$levelElevation(level);
        $d18200295c6c5d42$var$forEachAreaCell(level, (x, y)=>{
            const row = elevation[y].split('');
            row[x] = String((0, $490d5ef8e3640c94$export$4465cd453867bc23)($d18200295c6c5d42$var$selectedHeight));
            elevation[y] = row.join('');
        });
        $d18200295c6c5d42$var$renderGrid();
        $d18200295c6c5d42$var$renderValidation();
        $d18200295c6c5d42$var$saveState();
        return;
    }
    if (!confirm(`Fill ${areaLabel} with current tile?`)) return;
    const char = (0, $8d78eed0340873cb$export$34ddeca23ac38a20)[$d18200295c6c5d42$var$selectedTileType].mapChar;
    $d18200295c6c5d42$var$forEachAreaCell(level, (x, y)=>{
        const row = level.grid[y].split('');
        row[x] = char;
        level.grid[y] = row.join('');
    });
    $d18200295c6c5d42$var$prunePins(level);
    $d18200295c6c5d42$var$renderGrid();
    $d18200295c6c5d42$var$renderValidation();
    $d18200295c6c5d42$var$saveState();
}
/** Every `(x, y)` the fill/paint tools should act on: the active selection, or the whole grid. */ function $d18200295c6c5d42$var$forEachAreaCell(level, fn) {
    if ($d18200295c6c5d42$var$selection) {
        for(let y = $d18200295c6c5d42$var$selection.y0; y <= $d18200295c6c5d42$var$selection.y1; y++)for(let x = $d18200295c6c5d42$var$selection.x0; x <= $d18200295c6c5d42$var$selection.x1; x++)fn(x, y);
        return;
    }
    const columns = (0, $628bcc8876065384$export$2f192c6eccb63fc0)(level.grid);
    for(let y = 0; y < level.grid.length; y++)for(let x = 0; x < columns; x++)fn(x, y);
}
function $d18200295c6c5d42$var$getExportCourseData() {
    return (0, $54c87622d515a545$export$6c4275ae8c690ec1)($d18200295c6c5d42$var$courseData);
}
// --- Import / Export ---
async function $d18200295c6c5d42$var$saveToFile() {
    const courseId = $d18200295c6c5d42$var$courseData.id || 'new_course';
    const dataStr = JSON.stringify($d18200295c6c5d42$var$getExportCourseData(), null, 4);
    try {
        // Use File System Access API if supported, prompts user for path and filename
        if (window.showSaveFilePicker) {
            const handle = await window.showSaveFilePicker({
                suggestedName: courseId + '.pocketgolf',
                types: [
                    {
                        description: 'Pocket Golf Course',
                        accept: {
                            'application/json': [
                                '.pocketgolf'
                            ]
                        }
                    }
                ]
            });
            const writable = await handle.createWritable();
            await writable.write(dataStr);
            await writable.close();
            $d18200295c6c5d42$var$showSaveStatus('Saved to chosen file successfully');
        } else {
            // Fallback to classic download
            const blob = new Blob([
                dataStr
            ], {
                type: 'text/json;charset=utf-8'
            });
            const url = URL.createObjectURL(blob);
            const downloadAnchorNode = document.createElement('a');
            downloadAnchorNode.setAttribute('href', url);
            downloadAnchorNode.setAttribute('download', courseId + '.pocketgolf');
            document.body.appendChild(downloadAnchorNode);
            downloadAnchorNode.click();
            downloadAnchorNode.remove();
            URL.revokeObjectURL(url);
            $d18200295c6c5d42$var$showSaveStatus('Downloaded to file');
        }
    } catch (err) {
        if (err.name !== 'AbortError') alert('Save File failed: ' + err.message);
    }
}
function $d18200295c6c5d42$var$copyJsonToClipboard() {
    const dataStr = JSON.stringify($d18200295c6c5d42$var$getExportCourseData(), null, 4);
    navigator.clipboard.writeText(dataStr).then(()=>{
        $d18200295c6c5d42$var$showSaveStatus('JSON copied to clipboard!');
    }).catch((err)=>{
        alert('Failed to copy text: ' + err);
    });
}
/**
 * Show or hide the course properties column.
 *
 * A hole can be 50 tiles wide now, which is 1500px of grid on a column that used to have 450px of
 * form beside it permanently. Collapsing gives that back without touching the zoom: the cells stay
 * 30px, because shrinking them is what makes a grid unreadable.
 *
 * The state is a view preference, so it is kept apart from `pocketGolfCourse` — a course is a file an
 * author exports and shares, and whether their sidebar was open is nobody else's business.
 */ function $d18200295c6c5d42$var$togglePropertiesPanel(collapsed) {
    const layout = document.getElementById('editorLayout');
    if (!layout) return;
    // An explicit argument is the restore, which has to land instantly; no argument is a click, which
    // is the only thing worth animating.
    if (collapsed === undefined) layout.classList.add($d18200295c6c5d42$var$PROPERTIES_ANIMATED);
    const next = collapsed === undefined ? !layout.classList.contains($d18200295c6c5d42$var$PROPERTIES_COLLAPSED) : collapsed;
    layout.classList.toggle($d18200295c6c5d42$var$PROPERTIES_COLLAPSED, next);
    const toggle = document.getElementById('propertiesToggle');
    if (toggle) {
        toggle.setAttribute('aria-expanded', String(!next));
        toggle.title = next ? 'Show the course properties' : 'Hide the course properties';
    }
    const chevron = document.getElementById('propertiesToggleChevron');
    if (chevron) chevron.innerHTML = next ? '&raquo;' : '&laquo;';
    try {
        localStorage.setItem($d18200295c6c5d42$var$PROPERTIES_STORAGE_KEY, next ? '1' : '0');
    } catch (e) {
    // A browser with storage blocked still gets a working toggle; it just forgets between visits.
    }
}
/** The collapse state from last time, applied before the first paint of the layout. */ function $d18200295c6c5d42$var$restorePropertiesPanel() {
    let stored = null;
    try {
        stored = localStorage.getItem($d18200295c6c5d42$var$PROPERTIES_STORAGE_KEY);
    } catch (e) {
        stored = null;
    }
    $d18200295c6c5d42$var$togglePropertiesPanel(stored === '1');
}
function $d18200295c6c5d42$var$copyHoleGrid() {
    if ($d18200295c6c5d42$var$currentLevelIndex < 0) return;
    const level = $d18200295c6c5d42$var$courseData.levels[$d18200295c6c5d42$var$currentLevelIndex];
    const gridStr = JSON.stringify(level.grid, null, 4);
    navigator.clipboard.writeText(gridStr).then(()=>{
        $d18200295c6c5d42$var$showSaveStatus('Hole grid copied to clipboard!');
    }).catch((err)=>{
        alert('Failed to copy text: ' + err);
    });
}
function $d18200295c6c5d42$var$loadFromFile(event) {
    const file = event.target.files[0];
    if (!file) return;
    $d18200295c6c5d42$var$loadCourseFile(file);
    // Reset the input value so the same file could be loaded again
    event.target.value = '';
}
/** Shared by the file picker and drag-and-drop: both just hand over a File. */ function $d18200295c6c5d42$var$loadCourseFile(file) {
    const reader = new FileReader();
    reader.onload = function(e) {
        try {
            const contents = e.target.result;
            $d18200295c6c5d42$var$courseData = (0, $54c87622d515a545$export$cc692388a4445984)(JSON.parse(contents));
            $d18200295c6c5d42$var$editMode = 'tiles';
            $d18200295c6c5d42$var$currentTool = 'brush';
            $d18200295c6c5d42$var$selection = null;
            $d18200295c6c5d42$var$currentLevelIndex = $d18200295c6c5d42$var$courseData.levels.length > 0 ? 0 : -1;
            $d18200295c6c5d42$var$updateAllLevelIds(); // Keep ID consistency even on load
            $d18200295c6c5d42$var$renderAll();
            $d18200295c6c5d42$var$saveState();
            $d18200295c6c5d42$var$showSaveStatus('Loaded from file successfully!');
        } catch (err) {
            alert('Error parsing JSON from file: ' + err.message);
        }
    };
    reader.readAsText(file);
}
// --- Drag and drop ---
/**
 * The browser's default action for a dropped file is to navigate to it, which would blow away the
 * editor's state. Every drag event has to be intercepted at the window level (not just the drop
 * zone) so a mis-aimed drop never reaches that default.
 */ function $d18200295c6c5d42$var$setupDragAndDrop() {
    [
        'dragenter',
        'dragover'
    ].forEach((eventName)=>{
        window.addEventListener(eventName, (e)=>{
            e.preventDefault();
            if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files')) document.body.classList.add('drag-over');
        });
    });
    window.addEventListener('dragleave', (e)=>{
        e.preventDefault();
        // relatedTarget is null once the pointer leaves the window itself, not just a child element -
        // without this check the overlay flickers off while dragging over child nodes.
        if (!e.relatedTarget) document.body.classList.remove('drag-over');
    });
    window.addEventListener('drop', (e)=>{
        e.preventDefault();
        document.body.classList.remove('drag-over');
        const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (!file) return;
        if (!/\.(pocketgolf|json)$/i.test(file.name)) {
            alert('Please drop a .pocketgolf or .json course file.');
            return;
        }
        $d18200295c6c5d42$var$loadCourseFile(file);
    });
}
function $d18200295c6c5d42$var$clearEntireCourse() {
    if (confirm('WARNING: This will delete ALL levels and course data and prepopulate 9 empty holes. Are you sure?')) {
        $d18200295c6c5d42$var$createDefaultCourse();
        $d18200295c6c5d42$var$selection = null;
        $d18200295c6c5d42$var$renderAll();
        $d18200295c6c5d42$var$saveState();
    }
}
// Initialize on load
// `type=module` defers this to after the document is parsed and before it is painted, which is where a
// remembered layout has to be applied: `onload` is late enough to show the panel open first.
$d18200295c6c5d42$var$restorePropertiesPanel();
window.onload = $d18200295c6c5d42$var$init;
// Expose functions to global scope for HTML event handlers
window.updateCourseData = $d18200295c6c5d42$var$updateCourseData;
window.loadFromFile = $d18200295c6c5d42$var$loadFromFile;
window.saveToFile = $d18200295c6c5d42$var$saveToFile;
window.copyJsonToClipboard = $d18200295c6c5d42$var$copyJsonToClipboard;
window.clearEntireCourse = $d18200295c6c5d42$var$clearEntireCourse;
window.toggleValidation = $d18200295c6c5d42$var$toggleValidation;
window.updateLevelData = $d18200295c6c5d42$var$updateLevelData;
window.addLevel = $d18200295c6c5d42$var$addLevel;
window.deleteLevel = $d18200295c6c5d42$var$deleteLevel;
window.copyHoleGrid = $d18200295c6c5d42$var$copyHoleGrid;
window.togglePropertiesPanel = $d18200295c6c5d42$var$togglePropertiesPanel;
window.changeGridSize = $d18200295c6c5d42$var$changeGridSize;
window.fillGrid = $d18200295c6c5d42$var$fillGrid;
window.changeCourseFormat = $d18200295c6c5d42$var$changeCourseFormat;
window.setEditMode = $d18200295c6c5d42$var$setEditMode;
window.setTool = $d18200295c6c5d42$var$setTool;
window.clearSelection = $d18200295c6c5d42$var$clearSelection;
window.clearTerrain = $d18200295c6c5d42$var$clearTerrain;


//# sourceMappingURL=mapEditor.90ac43ae.js.map
